import { calcDocument, D, dec, money, PAYMENT_METHODS, ZERO } from '@makila/fiscal';
export function cartTotals(lines, opts = {}) {
    for (const l of lines) {
        if (dec(l.taxRate).isZero() && !l.exemptionCode) {
            throw new SaleError('exemption_required', `"${l.description}": taxa 0% exige motivo de isenção`);
        }
    }
    return calcDocument({ lines, globalDiscountPct: opts.globalDiscountPct });
}
export class SaleError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SaleError';
    }
}
/** Sem rede não há confirmação automática: a referência é a única prova (proposta §3, pagamentos). */
const REFERENCE_REQUIRED = ['MP', 'EM', 'MK'];
/**
 * Reparte o que o cliente entregou pelo total. Só o numerário dá troco: um
 * M-Pesa ou um cartão acima do que falta pagar é recusado (devolver o
 * excesso exigiria uma reversão na operadora). O troco sai do numerário
 * gravado, para que os pagamentos do documento somem o total — a mesma
 * regra que o Makila impõe à FR/VD na emissão.
 */
export function settle(total, tenders) {
    const due = dec(total);
    let cash = ZERO;
    let nonCash = ZERO;
    for (const t of tenders) {
        if (!(t.method in PAYMENT_METHODS))
            throw new SaleError('unknown_method', `Meio de pagamento desconhecido: ${t.method}`);
        const a = dec(t.amount);
        if (!a.greaterThan(0) || a.decimalPlaces() > 2)
            throw new SaleError('invalid_amount', `Valor inválido em ${PAYMENT_METHODS[t.method]}`);
        if (REFERENCE_REQUIRED.includes(t.method) && !t.reference?.trim()) {
            throw new SaleError('reference_required', `${PAYMENT_METHODS[t.method]}: indique a referência da transacção`);
        }
        if (t.method === 'NU')
            cash = cash.plus(a);
        else
            nonCash = nonCash.plus(a);
    }
    const tendered = cash.plus(nonCash);
    if (tendered.lessThan(due))
        throw new SaleError('insufficient', `Faltam ${money(due.minus(tendered))} MT`);
    if (nonCash.greaterThan(due))
        throw new SaleError('overpay_non_cash', 'Só o numerário pode exceder o total (troco)');
    const change = tendered.minus(due);
    let changeLeft = change;
    const payments = [];
    for (const t of tenders) {
        let a = dec(t.amount);
        if (t.method === 'NU' && changeLeft.greaterThan(0)) {
            const take = D.min(a, changeLeft);
            a = a.minus(take);
            changeLeft = changeLeft.minus(take);
        }
        if (a.greaterThan(0))
            payments.push({ method: t.method, amount: money(a), reference: t.reference?.trim() || null });
    }
    return { payments, tendered: money(tendered), change: money(change) };
}
// ── Descontos ───────────────────────────────────────────────────────────────
/** Percentagem de desconto escrita por uma pessoa: 0–100, no máximo 2 casas. */
export function checkDiscountPct(v) {
    const d = dec(v);
    if (d.isNegative() || d.greaterThan(100) || d.decimalPlaces() > 2)
        throw new SaleError('invalid_discount', 'Desconto inválido (0 a 100%, até 2 casas decimais)');
    return d.toString();
}
/**
 * Desconto no total aplicado a cada linha: o desconto efectivo de uma linha
 * com d% e total g% é 1 − (1 − d)(1 − g) — 10% e 5% dão 14,5%. Assim cada
 * linha leva o que realmente se cobrou: a nota de crédito de uma linha
 * devolve exactamente o pago, e a nuvem confere com o mesmo calcLine.
 * (Arredonda por linha; um desconto calculado só no fim podia diferir em
 * cêntimos — este é o que se imprime e se assina.) O desconto efectivo
 * arredonda-se a 2 casas, a precisão das linhas do Makila (NUMERIC(5,2)), para
 * o documento entrar lá tal e qual: 33,33% e 12,47% dão 41,643749% → 41,64%.
 * A linha leva o valor arredondado, e é com ele que se cobra, assina e
 * confere — no terminal, na nuvem, no Makila e numa devolução.
 */
export function applyGlobalDiscount(lines, globalPct) {
    const g = new D(checkDiscountPct(globalPct)).div(100);
    if (g.isZero())
        return lines;
    return lines.map((l) => {
        if (l.promotionId || l.regulatedPrice || l.loyalty)
            return l; // promoções não acumulam; preço regulado não tem desconto (art. 64)
        const d = new D(checkDiscountPct(l.discountPct ?? 0)).div(100);
        const eff = new D(1).minus(new D(1).minus(d).times(new D(1).minus(g))).times(100);
        return { ...l, discountPct: eff.toDecimalPlaces(2).toString() };
    });
}
/** A venda tem algum desconto manual? (os de promoções não precisam de gerente) */
export const hasDiscount = (lines) => lines.some((l) => !l.promotionId && !l.loyalty && !dec(l.discountPct ?? 0).isZero());
//# sourceMappingURL=sale.js.map